import React, { useState, useEffect, useRef } from 'react'
import { X, Delete, CheckCircle2 } from 'lucide-react'
import { DEFAULT_QUICK_AMOUNTS } from '@/types/pos'

interface CashPaymentModalProps {
  total: number
  onComplete: (amountPaid: number, change: number) => void
  onClose: () => void
}

export const CashPaymentModal: React.FC<CashPaymentModalProps> = ({
  total,
  onComplete,
  onClose,
}) => {
  const [amountPaid, setAmountPaid] = useState<string>('')
  const [showKeypad, setShowKeypad] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)

  const isRefundDue = total < 0
  const effectiveTotal = Math.abs(total)
  const numericValue = parseFloat(amountPaid) || 0
  const change = isRefundDue ? 0 : Math.max(0, numericValue - total)
  const isValid = isRefundDue ? true : numericValue >= total

  // Quick amounts including exact and common round-ups
  const quickAmounts = [
    ...DEFAULT_QUICK_AMOUNTS,
    { value: Math.ceil(total), label: `Rs.${Math.ceil(total)}` },
    { value: Math.ceil(total / 5) * 5, label: `Rs.${Math.ceil(total / 5) * 5}` },
    { value: Math.ceil(total / 10) * 10, label: `Rs.${Math.ceil(total / 10) * 10}` },
  ].filter((v, i, arr) => 
    v.value >= total && arr.findIndex(a => a.value === v.value) === i
  ).slice(0, 6)

  const handleKeypadPress = (key: string) => {
    if (key === 'clear') {
      setAmountPaid('')
    } else if (key === 'backspace') {
      setAmountPaid(prev => prev.slice(0, -1))
    } else if (key === '.') {
      if (!amountPaid.includes('.')) {
        setAmountPaid(prev => prev + '.')
      }
    } else if (key === '00') {
      if (amountPaid && !amountPaid.includes('.')) {
        setAmountPaid(prev => prev + '00')
      }
    } else {
      // Prevent more than 2 decimal places
      const parts = amountPaid.split('.')
      if (parts.length === 2 && parts[1].length >= 2) return
      setAmountPaid(prev => prev + key)
    }
  }

  const handleQuickAmount = (amount: number) => {
    setAmountPaid(amount.toFixed(2))
  }

  const handleExactAmount = () => {
    setAmountPaid(total.toFixed(2))
  }

  const handleComplete = () => {
    if (isValid) {
      if (isRefundDue) {
        // For refund-due scenarios, amount paid is 0 and change is the refund amount
        onComplete(0, effectiveTotal)
      } else {
        onComplete(numericValue, change)
      }
    }
  }

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isValid) {
        handleComplete()
      } else if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isValid, numericValue])

  const keypadKeys = [
    ['7', '8', '9'],
    ['4', '5', '6'],
    ['1', '2', '3'],
    ['0', '00', '.'],
  ]

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-bold esales-text">{isRefundDue ? 'Refund to Customer' : 'Cash Payment'}</h3>
            <p className="text-xs text-muted-foreground mt-1">{isRefundDue ? 'Return credit exceeds purchase total' : 'Enter amount received'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Amount Display */}
        <div className="mb-6">
          {isRefundDue ? (
            <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/30 mb-3">
              <p className="text-xs text-orange-400 text-center mb-1 font-bold uppercase tracking-wider">Refund Due to Customer</p>
              <p className="text-3xl font-bold text-center text-orange-400">Rs.{effectiveTotal.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground text-center mt-1">Return credit exceeds purchase total</p>
            </div>
          ) : (
            <div className="p-4 bg-primary/10 rounded-xl border border-primary/30 mb-3">
              <p className="text-xs text-muted-foreground text-center mb-1">Amount Due</p>
              <p className="text-3xl font-bold text-center text-primary">Rs.{total.toFixed(2)}</p>
            </div>
          )}

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground">Rs.</span>
            <input
              ref={inputRef}
              type="text"
              value={amountPaid}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '')
                // Only allow one decimal point
                const parts = val.split('.')
                if (parts.length > 2) return
                if (parts.length === 2 && parts[1].length > 2) return
                setAmountPaid(val)
              }}
              placeholder="0.00"
              className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-right font-mono text-3xl font-bold focus:border-primary focus:outline-none"
            />
          </div>

          {/* Change Display */}
          {numericValue > 0 && (
            <div className={`mt-3 p-3 rounded-xl flex items-center justify-between ${
              isValid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
            }`}>
              <span className={`text-sm font-semibold ${isValid ? 'text-green-500' : 'text-red-500'}`}>
                {isValid ? 'Change Due:' : 'Amount Short:'}
              </span>
              <span className={`text-xl font-bold ${isValid ? 'text-green-500' : 'text-red-500'}`}>
                Rs.{isValid ? change.toFixed(2) : (total - numericValue).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Quick Amount Buttons */}
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">Quick Amounts</p>
          <div className="grid grid-cols-4 gap-2">
            <button
              onClick={handleExactAmount}
              className="py-2 px-3 bg-primary/20 text-primary hover:bg-primary/30 rounded-lg text-sm font-bold transition-colors"
            >
              Exact
            </button>
            {quickAmounts.slice(0, 3).map((qa, index) => (
              <button
                key={index}
                onClick={() => handleQuickAmount(qa.value)}
                className="py-2 px-3 bg-muted hover:bg-muted/80 rounded-lg text-sm font-semibold transition-colors"
              >
                {qa.label}
              </button>
            ))}
          </div>
        </div>

        {/* Keypad */}
        {showKeypad && (
          <div className="mb-4">
            <div className="grid grid-cols-3 gap-2">
              {keypadKeys.flat().map((key) => (
                <button
                  key={key}
                  onClick={() => handleKeypadPress(key)}
                  className="py-4 bg-muted hover:bg-muted/80 rounded-xl text-xl font-bold transition-colors active:scale-95"
                >
                  {key}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={() => handleKeypadPress('clear')}
                className="py-3 bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
              <button
                onClick={() => handleKeypadPress('backspace')}
                className="py-3 bg-red-500/20 text-red-500 hover:bg-red-500/30 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
              >
                <Delete className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Toggle Keypad */}
        <button
          onClick={() => setShowKeypad(!showKeypad)}
          className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          {showKeypad ? 'Hide Keypad' : 'Show Keypad'}
        </button>

        {/* Complete Button */}
        <button
          onClick={handleComplete}
          disabled={!isValid}
          className={`w-full py-4 rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 ${
            isRefundDue
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'bg-primary text-primary-foreground glow-effect'
          }`}
        >
          {isRefundDue ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              Process Refund (Rs.{effectiveTotal.toFixed(2)} to Customer)
            </>
          ) : isValid ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              Complete Payment {change > 0 && `(Change: Rs.${change.toFixed(2)})`}
            </>
          ) : (
            'Enter Amount'
          )}
        </button>
      </div>
    </div>
  )
}

export default CashPaymentModal
